import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Stack,
  Grid2,
  Link,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  Input,
  FormHelperText,
} from "@mui/material";
import { getUsersByOrgId, deleteUser } from "../../apis/apiList/userApi";
import { sendOrganizationInvite } from "../../apis/apiList/organizationApi";
import BeatLoader from "react-spinners/BeatLoader";
import theme from "../../theme/theme";
import AddUser from "./AddUser";
import EditUser from "./EditUser";
import { useMessage } from "../../context/MessageContext";
import { useApp } from "../../context/AppContext";

const override = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  top: "50%",
  left: "50%",
};

function ProductionTeam({
  handleProductionTeamList,
  productionTeamList,
  orgId,
  organization,
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayType, setDisplayType] = useState("");
  const [selectedEditUser, setSelectedEditUser] = useState({});
  const [updatedEditedUser, setUpdatedEditedUser] = useState({});
  const { showMessage } = useMessage();
  const [open, setOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedDeleteUser, setSelectedDeleteUser] = useState({});
  const [inviteData, setInviteData] = useState({ firstName: '', email: '' });
  const [inviteErrors, setInviteErrors] = useState({ firstName: '', email: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const { userDetails } = useApp();

  const handleDeleteDialog = (user) => {
    setSelectedDeleteUser(user);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  
  const handleInviteOpen = () => {
    setInviteOpen(true);
  };
  
  const handleInviteClose = () => {
    setInviteOpen(false);
    setInviteData({ firstName: '', email: '' });
    setInviteErrors({ firstName: '', email: '' });
  };
  
  const handleInviteChange = (e) => {
    const { name, value } = e.target;
    setInviteData({
      ...inviteData,
      [name]: value
    });
    
    // Clear error when user types
    if (inviteErrors[name]) {
      setInviteErrors({
        ...inviteErrors,
        [name]: ''
      });
    }
  };

  const handleDelete = async () => {
    console.log('selected delete user', selectedDeleteUser);
    try {
      const response = await deleteUser(selectedDeleteUser._id);
      console.log('response', response);
      if(response.success) {
        handleDeleteFilter(selectedDeleteUser._id)
        showMessage(response.message, 'success')
        setSelectedDeleteUser({});
        handleClose()
      }
    } catch (err) {
      console.log('err', err)
      if (err?.response) {
        showMessage(err?.response?.data?.message, "error");
      } else {
        showMessage(err?.message, "error");
      }
    }

    handleClose();
  };

  const handleDeleteFilter = () => {
    const newList = users.filter((user)=> user._id !== selectedDeleteUser._id);
    setUsers(newList);
  }

  useEffect(() => {
    async function fetchAllUser() {
      try {
        if (productionTeamList.length == 0) {
          const users = await getUsersByOrgId(orgId);
          console.log("users", users);
          setUsers(users.data);
          setLoading(false);
          handleProductionTeamList(users.data);
        } else {
          setUsers(productionTeamList);
          setLoading(false);
        }
      } catch (err) {
        console.log("err", err);
        showMessage(
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong",
          "error"
        );
      }
    }

    fetchAllUser();
  }, []);

  const handleAddUser = () => {
    setDisplayType("addUser");
  };

  const handleEditUser = (user) => {
    setSelectedEditUser(user);
    setDisplayType("editUser");
  };

  const handleCancel = () => {
    setDisplayType("");
  };

  const handleUpdatedUser = (userData)=>{
    const newUserData = users.map(user => user._id === userData._id ? userData : user);
    setUsers(newUserData);
  }
  
  const handleNewAddedUser = (userData)=>{
    let oldUsers = [...users];
    oldUsers.push(userData);
    setUsers(oldUsers);
  }
  
  const handleInviteSubmit = async () => {
    // Validate form
    let valid = true;
    const newErrors = { firstName: '', email: '' };
    
    if (!inviteData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      valid = false;
    }
    
    if (!inviteData.email.trim()) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(inviteData.email)) {
      newErrors.email = 'Email is invalid';
      valid = false;
    }
    
    if (!valid) {
      setInviteErrors(newErrors);
      return;
    }
    
    setInviteLoading(true);
    
    try {
      // Call API to send invitation email
      const response = await sendOrganizationInvite({
        firstName: inviteData.firstName,
        email: inviteData.email,
        organizationId: orgId
      }, {
        headers: { "x-access-token": userDetails.jwtToken },
      });
      
      // Show success message
      showMessage(response.message || `Invitation sent to ${inviteData.email} successfully!`, 'success');
      
      // Close dialog
      handleInviteClose();
    } catch (err) {
      console.error('Error sending invitation:', err);
      if (err?.response) {
        showMessage(err?.response?.data?.message || 'Failed to send invitation', 'error');
      } else {
        showMessage(err?.message || 'Failed to send invitation', 'error');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <div>
      {loading ? (
        <>
          <Box sx={override}>
            <BeatLoader
              color={theme.palette.primary.main}
              loading={loading}
              size={25}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </Box>
        </>
      ) : (
        <>
          <Box>
            {displayType == "" && (
              <>
                <Box mb={4}>
                  <Box textAlign={"end"} mb={1}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button 
                        variant="outlined" 
                        color="primary" 
                        onClick={handleInviteOpen}
                        sx={{ mr: 1 }}
                      >
                        Invite User
                      </Button>
                      <Button variant="contained" onClick={handleAddUser}>
                        + Add New User
                      </Button>
                    </Stack>
                  </Box>
                  <Divider />
                </Box>
                <Box>
                  {users && users.length > 0 ? (
                    <Box>
                      <Grid2 container width={"100%"} spacing={4}>
                        {users.map((user) => (
                          <Grid2 size={{ xs: 12, md: 6, lg: 4 }}>
                            <Box
                              key={user._id}
                              p={3}
                              border={1}
                              borderRadius={3}
                              borderColor={"#ccc"}
                              boxShadow={1}
                            >
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                alignItems="center"
                              >
                                {user?.profilePicture ? (
                                  <Avatar
                                    src={user.profilePicture}
                                    alt="User Image"
                                    variant="square"
                                    sx={{
                                      width: 95,
                                      height: 103,
                                      margin: "0px 16px 10px 0px !important",
                                    }}
                                  />
                                ) : (
                                  user.linkedinProfilePic && (
                                    <Avatar
                                      src={user.linkedinProfilePic}
                                      alt="User Image"
                                      variant="square"
                                      sx={{
                                        width: 95,
                                        height: 103,
                                        margin: "0px 16px 10px 0px !important",
                                      }}
                                    />
                                  ) ||
                                  !user.linkedinProfilePic && 
                                    <Box
                                      sx={{
                                        width: 95,
                                        height: 103,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "grey.300",
                                      }}
                                    >
                                      <Typography>No image</Typography>
                                    </Box>
                                )}
                                <Stack
                                  direction={"column"}
                                  gap={1}
                                  width={"100%"}
                                >
                                  <Typography variant="h3" marginBottom={1}>
                                    {user.firstName} {user.lastName}
                                  </Typography>
                                  <Typography variant="h6">
                                    <strong>Job Title:</strong> {user?.jobTitle}
                                  </Typography>
                                  <Divider />
                                  <Typography variant="h6">
                                    <strong>Email:</strong> {user.email}
                                  </Typography>
                                  <Divider />
                                </Stack>
                              </Stack>
                              <Typography variant="h6" mt={2}>
                                <strong>Phone:</strong> {user?.phone}
                              </Typography>
                              <Divider />
                              <Stack direction="row" spacing={2} mt={2}>
                                {(userDetails.role == "admin" || userDetails._id == user._id) && 
                                  <Typography
                                    sx={{ cursor: "pointer" }}
                                    color={theme.palette.primary.main}
                                    onClick={() => {
                                      handleEditUser(user);
                                    }}
                                  >
                                    Edit
                                  </Typography>
                                }
                                {userDetails.role == "admin" && 
                                  <Typography
                                    sx={{ cursor: "pointer" }}
                                    color={theme.palette.primary.main}
                                    onClick={() => {
                                      handleDeleteDialog(user);
                                    }}
                                  >
                                    Delete
                                  </Typography>
                                }
                              </Stack>
                            </Box>
                          </Grid2>
                        ))}
                      </Grid2>
                    </Box>
                  ) : (
                    <Box>
                      <Typography>No Users Found</Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}
            {displayType == "addUser" && (
              <AddUser
                handleCancel={handleCancel}
                orgId={orgId}
                organization={organization}
                handleNewAddedUser={handleNewAddedUser}
              />
            )}
            {displayType == "editUser" && (
              <EditUser
                handleCancel={handleCancel}
                orgId={orgId}
                organization={organization}
                user={selectedEditUser}
                handleUpdatedUser={handleUpdatedUser}
              />
            )}
          </Box>
          {/* Dialog for delete organization */}
          <Dialog open={open} onClose={handleClose}>
            <DialogTitle>
              Are you sure you want to delete user
              <strong>
                {` ${selectedDeleteUser.firstName} ${selectedDeleteUser.lastName}`}
              </strong>
              ?
            </DialogTitle>
            <DialogActions>
              <Button onClick={handleClose} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDelete} color="error">
                Delete
              </Button>
            </DialogActions>
          </Dialog>
          
          {/* Invite User Dialog */}
          <Dialog open={inviteOpen} onClose={handleInviteClose} maxWidth="sm" fullWidth>
            <DialogTitle>Invite User to Organization</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Please enter the details of the person you want to invite to join this organization.
              </DialogContentText>
              <FormControl fullWidth margin="dense" error={!!inviteErrors.firstName}>
                <InputLabel htmlFor="firstName">First Name</InputLabel>
                <Input
                  id="firstName"
                  name="firstName"
                  value={inviteData.firstName}
                  onChange={handleInviteChange}
                  autoFocus
                />
                {inviteErrors.firstName && <FormHelperText>{inviteErrors.firstName}</FormHelperText>}
              </FormControl>
              <FormControl fullWidth margin="dense" error={!!inviteErrors.email}>
                <InputLabel htmlFor="email">Email Address</InputLabel>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={inviteData.email}
                  onChange={handleInviteChange}
                />
                {inviteErrors.email && <FormHelperText>{inviteErrors.email}</FormHelperText>}
              </FormControl>
            </DialogContent>
            <DialogActions>
            <Button onClick={handleInviteClose} color="primary" disabled={inviteLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleInviteSubmit} 
              color="primary" 
              variant="contained"
              disabled={inviteLoading}
              startIcon={inviteLoading ? <BeatLoader size={8} color={theme.palette.primary.main} /> : null}
            >
              {inviteLoading ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default ProductionTeam;
